import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { CheckCircle2, Loader2, ArrowLeft, AlertCircle } from 'lucide-react';
import { useAuth } from '../auth/AuthContext';
import { SEO } from '../seo/SEO';
import axios from 'axios';
import { supabase } from '../../lib/supabaseClient';

export const PaymentDirectPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [subscriptionDetails, setSubscriptionDetails] = useState<{
    planName: string;
    credits: number;
    expiryDate: string;
  } | null>(null);
  
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  
  // Get parameters from URL
  const reference = queryParams.get('reference');
  const provider = queryParams.get('provider');
  const amount = queryParams.get('amount');
  const email = queryParams.get('email');
  const planName = queryParams.get('plan');
  
  useEffect(() => {
    const processPayment = async () => {
      if (!user) {
        navigate('/signin');
        return;
      }
      
      if (!reference || !provider) {
        setError('Invalid payment parameters');
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        
        // Verify payment based on provider
        if (provider.toLowerCase() === 'paystack') {
          const { data: { session } } = await supabase.auth.getSession();
          if (!session?.access_token) {
            throw new Error('Authentication required');
          }
          
          const response = await axios.post('/api/payments/verify-paystack-payment', 
            { reference },
            {
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${session.access_token}`
              }
            }
          );
          
          if (response.data.success) {
            setSuccess(true);
            setSubscriptionDetails({
              planName: response.data.planName || planName || 'Subscription',
              credits: response.data.credits || 0,
              expiryDate: new Date(response.data.expiryDate).toLocaleDateString()
            });
          } else {
            throw new Error('Payment verification failed');
          }
        } else {
          // For other providers, just show success for now
          setSuccess(true);
          setSubscriptionDetails({
            planName: planName || 'Subscription',
            credits: 100, // Default value
            expiryDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toLocaleDateString()
          });
        }
      } catch (error) {
        console.error('Payment verification error:', error);
        setError(error instanceof Error ? error.message : 'Failed to verify payment');
      } finally {
        setLoading(false);
      }
    };
    
    processPayment();
  }, [user, reference, provider, navigate, planName]);
  
  return (
    <>
      <SEO 
        title="Processing Payment | Banga.ai"
        description="Processing your payment. Please wait while we verify your transaction."
      />
      
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8 transition-colors">
        <div className="max-w-md mx-auto">
          <div className="mb-8">
            <button
              onClick={() => navigate('/billing')}
              className="inline-flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Billing
            </button>
          </div>
          
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 text-center">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-8">
                <Loader2 className="w-12 h-12 text-blue-500 animate-spin mb-4" />
                <p className="text-gray-600 dark:text-gray-300">Processing your payment...</p>
              </div>
            ) : error ? (
              <div className="py-8">
                <div className="bg-red-100 dark:bg-red-900/20 p-4 rounded-lg mb-4 flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-red-700 dark:text-red-400">{error}</p>
                </div>
                <button
                  onClick={() => navigate('/billing')}
                  className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Return to Billing
                </button>
              </div>
            ) : success ? (
              <div className="py-8">
                <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                  Payment Successful!
                </h2>
                <p className="text-gray-600 dark:text-gray-300 mb-6">
                  Thank you for your purchase. Your subscription is now active.
                </p>
                
                {subscriptionDetails && (
                  <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg text-left mb-6">
                    <div className="grid grid-cols-2 gap-2">
                      <div className="text-gray-600 dark:text-gray-400">Plan:</div>
                      <div className="text-gray-900 dark:text-white font-medium">
                        {subscriptionDetails.planName}
                      </div>
                      
                      <div className="text-gray-600 dark:text-gray-400">Credits:</div>
                      <div className="text-gray-900 dark:text-white font-medium">
                        {subscriptionDetails.credits}
                      </div>
                      
                      <div className="text-gray-600 dark:text-gray-400">Expires:</div>
                      <div className="text-gray-900 dark:text-white font-medium">
                        {subscriptionDetails.expiryDate}
                      </div>
                    </div>
                  </div>
                )}
                
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <button
                    onClick={() => navigate('/')}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Start Searching
                  </button>
                  <button
                    onClick={() => navigate('/billing')}
                    className="px-6 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    View Subscription
                  </button>
                </div>
              </div>
            ) : (
              <div className="py-8">
                <AlertCircle className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                  Payment Status Unknown
                </h2>
                <p className="text-gray-600 dark:text-gray-300 mb-6">
                  We couldn't determine the status of your payment. Please check your email for confirmation.
                </p>
                <button
                  onClick={() => navigate('/billing')}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Return to Billing
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};